import { useMutation, useQuery } from '@apollo/client';
import { InsertDriveFileOutlined } from '@mui/icons-material';
import { FormSchemaProvider, FormSchemaProviderProps } from '@procureai/ui-form-mui';
import {
  Widget,
  WidgetContent,
  WidgetHeader,
  WidgetIcon,
  WidgetEditActions,
  useUuid,
  useScrollIntoView,
  useCustomSnackbar,
} from '@procureai/ui-mui';
import { difference, isEmpty, map, prop, all } from 'ramda';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { FilesByDemandQuery } from '@/generated/gql/graphql';
import { I18nNamespace } from '~/common/types';
import { useModuleContext } from '~/components/ModuleContextProvider';
import { isDemandEditable } from '~/util/demand/isDemandEditable';
import { isNegotiationCaseEditable } from '~/util/negotiationCase/isNegotiationCaseEditable';

import {
  DemandDocumentContent,
  DemandDocumentContentProps,
} from './components/DemandDocumentContent/DemandDocumentContent';
import { DemandDocumentForm } from './components/DemandDocumentForm/DemandDocumentForm';
import {
  demandDocumentFormDataDefault,
  demandDocumentFormDataSchema,
  DemandDocumentFormData,
} from './components/DemandDocumentForm/DemandDocumentForm.schema';
import { FILES_DEMAND_CREATE_MUTATION } from './DemandDocumentCreateMutation.gql';
import { FILES_DEMAND_QUERY } from './DemandDocumentQuery.gql';
import { FILES_DEMAND_UPDATE_MUTATION } from './DemandDocumentsUpdateMutation.gql';

interface DemandDocumentWidgetProps {
  hasParentForm?: boolean;
}

const EMPTY_CONTENTDATA = [] as DemandDocumentContentProps['files'];

const isDemandDocumentEnabled = (data: FilesByDemandQuery | undefined) =>
  data?.tesseract.demand?.permissions.write &&
  isDemandEditable(data) &&
  all(isNegotiationCaseEditable, data?.tesseract.demand?.negotiationCases.nodes);

export const DemandDocumentWidget = ({ hasParentForm }: DemandDocumentWidgetProps) => {
  const { t } = useTranslation(I18nNamespace.widgets, { keyPrefix: 'DemandDocumentWidget' });
  const { entityId } = useModuleContext();
  const { data, loading } = useQuery(FILES_DEMAND_QUERY, {
    variables: { id: entityId },
  });

  const formId = useUuid();
  const [saving, setSaving] = useState(false);

  const [widgetRef, active] = useScrollIntoView({
    hash: 'demand-attachment',
  });

  const { onError } = useCustomSnackbar();

  const [editOpen, setEditOpen] = useState(hasParentForm);

  const [addFileToDemand] = useMutation(FILES_DEMAND_CREATE_MUTATION, {
    onError,
  });

  const [updateFilesOnDemand] = useMutation(FILES_DEMAND_UPDATE_MUTATION, {
    onError,
  });

  const editDisabled = !isDemandDocumentEnabled(data);
  const handleEditToggle = useCallback(() => {
    setEditOpen((prev) => !prev);
  }, []);

  const files = data?.tesseract.demand.files;
  const contentData = useMemo(() => (hasParentForm ? EMPTY_CONTENTDATA : files), [files, hasParentForm]);

  const contentDataForm: DemandDocumentFormData = useMemo(() => {
    return {
      files:
        contentData?.map((file) => ({
          fileId: file.id,
          isSharedWithSupplier: file.isSharedWithSupplier,
          isDeleted: false,
          hasNegotiationCase: Boolean(file.negotiationCaseId && file.negotiationCaseSequence),
        })) ?? [],
    };
  }, [contentData]);

  const handleEditApply: FormSchemaProviderProps<DemandDocumentFormData>['onSubmit'] = async (submitData) => {
    setSaving(true);
    const idsInitial = map(prop('id'), contentData ?? []);
    const idsEditing = map(prop('fileId'), submitData.files ?? []);
    const fileIdsToAdd: string[] = difference(idsEditing, idsInitial);

    if (!isEmpty(fileIdsToAdd)) {
      await addFileToDemand({
        variables: {
          id: entityId,
          data: fileIdsToAdd.map((fileId) => ({ fileId })),
        },
      });
    }

    await updateFilesOnDemand({
      variables: {
        id: entityId,
        files: submitData.files.map(({ hasNegotiationCase: _, ...file }) => file),
      },
      refetchQueries: [
        {
          query: FILES_DEMAND_QUERY,
          variables: { id: entityId },
        },
      ],
      awaitRefetchQueries: true,
    });

    setSaving(false);
    setEditOpen(false);
  };

  let content = null;

  if (editOpen && data) {
    content = <DemandDocumentForm loading={loading} />;

    if (!hasParentForm) {
      content = (
        <FormSchemaProvider<DemandDocumentFormData>
          id={formId}
          data={contentDataForm}
          schema={demandDocumentFormDataSchema}
          defaultValues={demandDocumentFormDataDefault}
          onSubmit={handleEditApply}
        >
          {content}
        </FormSchemaProvider>
      );
    }
  } else if (loading || contentData) {
    content = <DemandDocumentContent files={contentData ?? []} loading={loading} />;
  }

  return (
    <Widget ref={widgetRef} active={active} data-intercom-target="DemandDocumentWidget">
      <WidgetHeader
        title={t('title')}
        icon={<WidgetIcon color="primary" icon={<InsertDriveFileOutlined fontSize="small" />} />}
        actionsRight={
          <WidgetEditActions
            formId={formId}
            open={editOpen}
            disabled={editDisabled}
            saving={saving}
            onToggle={handleEditToggle}
          />
        }
      />
      <WidgetContent>{content}</WidgetContent>
    </Widget>
  );
};
